import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BookOpenIcon, TrashIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { orpc } from '@/api/rpc/client';
import { IconTooltip } from '@/components/common/IconTooltip';

/** One Workbook on the Workbooks list, with Delete when the user may delete. */
export function WorkbookCard({
  workbook,
  canDelete,
}: {
  workbook: { id: string; name: string; updatedAt: string };
  canDelete: boolean;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation(
    orpc.workbooks.delete.mutationOptions({
      onSuccess: async () => {
        setConfirming(false);
        await queryClient.invalidateQueries({
          queryKey: orpc.workbooks.list.key(),
        });
      },
    }),
  );
  return (
    <div className="relative">
      <Link
        to="/workbooks/$workbookId"
        params={{ workbookId: workbook.id }}
        className="block rounded-lg border bg-card p-3 hover:bg-muted"
      >
        <BookOpenIcon className="mb-2 size-5 text-primary" />
        <h2 className="font-medium">{workbook.name}</h2>
        <p className="text-xs text-muted-foreground">
          {new Date(workbook.updatedAt).toLocaleString()}
        </p>
      </Link>
      {canDelete && (
        <IconTooltip>
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 size-7 text-muted-foreground"
            aria-label={gt('Delete workbook', {
              context: 'delete workbook action',
            })}
            onClick={() => {
              remove.reset();
              setConfirming(true);
            }}
          >
            <TrashIcon />
          </Button>
        </IconTooltip>
      )}
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => remove.mutate({ workbookId: workbook.id })}
        title={<T context="delete workbook dialog title">Delete workbook?</T>}
        message={gt(
          'This permanently deletes {name} with its files, versions and run history.',
          {
            name: workbook.name,
            context: 'delete workbook dialog message',
          },
        )}
        confirmText={gt('Delete', { context: 'delete workbook confirm' })}
        cancelText={gt('Cancel', { context: 'delete workbook cancel' })}
        loading={remove.isPending}
        dangerous
        closeOnConfirm={false}
      >
        {remove.error && (
          <p role="alert" className="text-sm text-destructive">
            {remove.error.message}
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}
